import { useState, useEffect, useCallback } from 'react';
import { useSocket } from './socket';
import { useAuth } from './auth';
import type { Notification } from '@/types/user';
import { useSnackbar } from 'notistack';

export const useNotifications = () => {
    const { user } = useAuth();
    const { socket } = useSocket();
    const { enqueueSnackbar } = useSnackbar();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/users/notifications?limit=20`, {
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success && data.data && data.data.data) {
                setNotifications(data.data.data);
                setUnreadCount(data.data.data.filter((n: Notification) => n.status === 'UNREAD' || !n.readAt).length);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    useEffect(() => {
        if (!socket || !user) return;

        const handleNewNotification = (notification: Notification) => {
            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            enqueueSnackbar(notification.title, { variant: 'info' });
        };

        const handleReadNotification = (readNotification: Notification) => {
            setNotifications(prev => prev.map(n => n.id === readNotification.id ? readNotification : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        };

        const handleReadAll = () => {
            setNotifications(prev => prev.map(n => ({ ...n, status: 'READ', readAt: new Date().toISOString() })));
            setUnreadCount(0);
        };

        socket.on(`notification:${user.id}`, handleNewNotification);
        socket.on(`notification_read:${user.id}`, handleReadNotification);
        socket.on(`notification_read_all:${user.id}`, handleReadAll);

        return () => {
            socket.off(`notification:${user.id}`, handleNewNotification);
            socket.off(`notification_read:${user.id}`, handleReadNotification);
            socket.off(`notification_read_all:${user.id}`, handleReadAll);
        };
    }, [socket, user, enqueueSnackbar]);

    const markAsRead = async (id: string) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/users/notifications/${id}/read`, {
                method: 'PUT',
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success) {
                // Update optimistically if socket doesn't fire immediately
                setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'READ', readAt: new Date().toISOString() } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/users/notifications/read-all`, {
                method: 'PUT',
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success) {
                setNotifications(prev => prev.map(n => ({ ...n, status: 'READ', readAt: new Date().toISOString() })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    return {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        refetch: fetchNotifications
    };
};
